// Global Instructions Rule Applied!
//
// Where a cited interaction opens — the pure half of useOpenInteraction,
// kept router-free so the signal / memory cards (and their jest suites) can
// decide link-vs-text without pulling the tab manager in.
//
// An interaction has no form of its own — it lives in its parent record's
// timeline (Dashboard tab). The app deep-links into that timeline with
// `?interaction=<id>` on the parent's full-form URL (InteractionsSection),
// the same route the proposal review Open button takes
// (agent_access/utils/proposalPresentation.tsx `openTargetFor`).
import { TABLE_ACCOUNTS, TABLE_DEALS, TABLE_PROSPECTS } from '../../../state_manager/globals.ts'

export interface InteractionOpenTarget {
  /** Tab-manager module — openFormInTab maps accounts → companies, prospects → fundraising. */
  module: string
  id: string
  query: Record<string, string>
}

/** Subjects whose full form carries an interactions timeline on its Dashboard. */
const TIMELINE_SUBJECTS = new Set<string>([TABLE_ACCOUNTS, TABLE_DEALS, TABLE_PROSPECTS])

/** The timeline a citation says it lives on (RPC `timeline_*` / `source_timeline_*`). */
export interface TimelineRef {
  table?: string | null
  id?: string | null
}

/**
 * The full-form target that shows `interactionId`: the timeline the citation
 * carries when it names one (an interaction the agent read off another
 * record — a prospect signal citing the investor account's email), else
 * `contextTable/contextId` when that record has a timeline. Null when there is
 * nowhere to open it. `tab=dashboard` is carried so a form open on another
 * tab (Agent, Execution) switches back to the Dashboard, where the timeline
 * lives.
 */
export const interactionOpenTarget = (
  contextTable: string | null | undefined,
  contextId: string | null | undefined,
  interactionId: string | null | undefined,
  timeline?: TimelineRef | null
): InteractionOpenTarget | null => {
  if (!interactionId) return null
  const query = { interaction: interactionId, tab: 'dashboard' }
  if (timeline?.table && timeline?.id && TIMELINE_SUBJECTS.has(timeline.table)) {
    return { module: timeline.table, id: timeline.id, query }
  }
  if (!contextTable || !contextId || !TIMELINE_SUBJECTS.has(contextTable)) return null
  return { module: contextTable, id: contextId, query }
}

